import { POLICY } from "./policies";

const syncPromises = {};

function shouldSync({
  policy,
  isValid,
  isExpired
}) {
  // Nunca se sincronizó
  if (!isValid) return true;

  switch (policy) {
    case POLICY.EVENT:
      return false;
    case POLICY.TTL:
      return isExpired;
    case POLICY.ONCE:
      return false;
    default:
      return true;
  }
}

export async function invalidateSync(repository, scope, key = undefined) {
  const scopeKey = key === undefined ? scope : `${scope}-${key}`; // Permite invalidar por scope o por scopeKey
  const syncGlobal = await repository.getById(scopeKey);
  const nextGlobalGeneration = (syncGlobal?.generation ?? 0) + 1;

  await repository.upsertMerge({
    id: scopeKey,
    generation: nextGlobalGeneration
  });
  return true;
}

function runSync(key, fn) {
  if (!syncPromises[key]) {
    syncPromises[key] = fn().finally(() => {
      syncPromises[key] = null;
    });
  }

  return syncPromises[key];
}

export async function runResourceSync(syncEngine, scope, key, {
  cached,
  repository,
  onCall,
  onFinally,
  fetchRemote,
  selectItems,
  mapItems,
  selectRemoved,
  selectServerTime,
  persist,
  mode = "mirror",
  scopeIndex,
  scopeValue,
  scopeFilter,
  policy = POLICY.EVENT,
  ttl = null,
  force = false
}) {
  const scopeKey = `${scope}-${key}`;
  return runSync(scopeKey, async () => {
    if (!syncEngine.isLeader()) {
      return cached ?? [];
    }
    const syncGlobal = await syncEngine.repository.getById(scope);
    const globalGeneration = syncGlobal?.generation ?? 0;

    const syncLocal = await syncEngine.repository.getById(scopeKey);
    const localGeneration = syncLocal?.generation;
    const localHasSynced = syncLocal?.hasSynced;

    const isValid =
      localGeneration === globalGeneration &&
      localHasSynced === true;

    const lastSyncAt = syncLocal?.lastSyncAt;
    const now = Date.now();

    const isExpired =
      ttl != null &&
      lastSyncAt != null &&
      now - lastSyncAt > ttl;
    
    const mustSync = force || shouldSync({
      policy,
      isValid,
      isExpired,
    });
      
    if (!mustSync) {
      return cached ?? [];
    }

    if (typeof onCall === 'function') onCall();
    const response = await fetchRemote({ lastSyncAt }).finally(() => {
      if (typeof onFinally === 'function') onFinally();
    });

    let items = selectItems(response) ?? [];
    if (mapItems) {
      items = mapItems(items);
    }

    const removed = selectRemoved?.(response) ?? [];

    const serverTime = selectServerTime?.(response) ?? Date.now();

    if (typeof persist === "function") {
      await persist({
        items,
        removed,
        response,
        scope,
        key,
        scopeKey,
        syncEngine
      });
    } else {
      if (mode === "mirror") {
        await repository.mirror(items, { scopeIndex, scopeValue, scopeFilter });
      } else if (mode === "append") {
        await repository.upsertMany(items);
      }
      if (removed.length && repository.deleteMany) {
        await repository.deleteMany(removed);
      }
    }


    await syncEngine.repository.upsertMerge({
      id: scopeKey,
      generation: globalGeneration,
      lastSyncAt: serverTime,
      hasSynced: true
    });

    return items;
  });
}